// Feedback after every operation (ADR-109): sendCommand reports each result here; the ToastHost names the saved
// record and the phone vibrates — a short success pattern, or an error pattern for a refusal (which itself stays in
// the sheet as a Notice). Haptics are a no-op on the web.
import { Capacitor } from '@capacitor/core';

export type CommandOutcome =
  | { ok: true; operation: string; result: unknown }
  | { ok: false; operation: string; code: string };
type Listener = (outcome: CommandOutcome) => void;

const listeners = new Set<Listener>();

export function onCommand(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function reportCommand(outcome: CommandOutcome) {
  listeners.forEach((l) => l(outcome));
  void vibrate(outcome.ok);
}

async function vibrate(ok: boolean) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: ok ? NotificationType.Success : NotificationType.Error });
  } catch {
    // A phone without a vibrator: nothing to do.
  }
}

/** The record a result names: its number, or the numbers of the invoices it made. */
export function recordOf(result: unknown): string {
  if (!result || typeof result !== 'object') return '';
  const r = result as { number?: unknown; sales?: { number?: unknown }[] };
  if (typeof r.number === 'string') return r.number;
  if (Array.isArray(r.sales))
    return r.sales
      .map((s) => s.number)
      .filter((n) => typeof n === 'string')
      .join(' · ');
  return '';
}
